/**
 * Slip Log Service
 * 
 * Service สำหรับจัดการข้อมูลสลิป
 */

import { queryClass } from '@/services/adapters/parse/parse-client'
import type {
  SlipLog,
  SlipLogQueryParams,
  SlipLogQueryResult,
  SlipLogStats,
  UploadSlipData,
  SmartSlipUploadResult,
} from './slip-log.types'

// ============================================
// Mapping Functions
// ============================================

function mapToSlipLog(raw: any): SlipLog {
  // ดึง sender bank info
  const senderBankId = extractBankId(raw, 'sender')
  const receiverBankId = extractBankId(raw, 'receiver')
  
  return {
    id: raw.objectId,
    objectId: raw.objectId,
    
    // Slip Data
    amount: raw.amount || 0,
    datetime: raw.datetime,
    date: raw.date,
    time: raw.time,
    transfer_id: raw.transfer_id,
    
    // Sender Info
    sender: raw.sender,
    senderBankId,
    senderBankAccount: extractBankAccount(raw, 'sender'),
    senderName: extractName(raw, 'sender'),
    fromAccount: raw.fromAccount,
    
    // Receiver Info
    receiver: raw.recieveer || raw.receiver,
    receiverBankId,
    receiverBankAccount: extractBankAccount(raw, 'receiver'),
    receiverName: extractName(raw, 'receiver'),
    toAccount: raw.toAccount,

    // Web Bank Account Info (Primary Deposit bank)
    webBankName: raw.web_bank_name,
    webBankAccount: raw.web_bank_account,
    webBankTag: raw.web_bank_tag,
    webBankId: raw.web_bank_id,

    // Status
    status: raw.status,
    verificationStatus: raw.verificationStatus,
    returnedMessage: raw.returnedMessage,
    
    // Meta
    imagePath: raw.imagePath,
    payload: raw.payload,
    provider: raw.provider,
    apiMethod: raw.apiMethod,
    apiHttpStatus: raw.apiHttpStatus,
    uploadFrom: raw.uploadFrom || 'Member',
    
    // Duplicate Check
    slip_new: raw.slip_new,
    slip_count: raw.slip_count,
    
    // Relations
    member_id: raw.mamer_id?.objectId || raw.member_id,
    memberName: raw.mamer_id ? `${raw.mamer_id.firstname || ''} ${raw.mamer_id.lastname || ''}`.trim() : '',
    memberUsername: raw.mamer_id?.agent_username,
    
    // Raw Data
    data: raw.data,
    rawApiResponse: raw.rawApiResponse,
    
    // Timestamps
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    deletedAt: raw.deletedAt,
  }
}

// Helper: Extract bank ID from nested data
function extractBankId(raw: any, type: 'sender' | 'receiver'): string {
  try {
    // Check normalized data
    if (type === 'sender' && raw.data?.sender_bank_id) {
      return raw.data.sender_bank_id
    }
    if (type === 'receiver' && raw.data?.receiver_bank_id) {
      return raw.data.receiver_bank_id
    }
    
    // Check rawApiResponse
    if (raw.rawApiResponse?.data?.[type]?.bank?.id) {
      return raw.rawApiResponse.data[type].bank.id
    }
    
    // Check nested data
    if (raw.data?.data?.[type]?.bank?.id) {
      return raw.data.data[type].bank.id
    }
    
    return ''
  } catch {
    return ''
  }
}

// Helper: Extract bank account from nested data
function extractBankAccount(raw: any, type: 'sender' | 'receiver'): string {
  try {
    // Check normalized data
    if (type === 'sender' && raw.data?.sender_account) {
      return raw.data.sender_account
    }
    if (type === 'receiver' && raw.data?.account) {
      return raw.data.account
    }
    
    // Check rawApiResponse
    const accountPath = raw.rawApiResponse?.data?.[type]?.account
    if (accountPath?.bank?.account) {
      return accountPath.bank.account
    }
    
    // Fallback to direct fields
    if (type === 'sender') {
      return raw.sender || raw.fromAccount || ''
    }
    return raw.recieveer || raw.receiver || raw.toAccount || ''
  } catch {
    return ''
  }
}

// Helper: Extract name from nested data
function extractName(raw: any, type: 'sender' | 'receiver'): string {
  try {
    // Check normalized data
    if (type === 'sender' && raw.data?.sender_name) {
      return raw.data.sender_name
    }
    if (type === 'receiver' && raw.data?.merchant) {
      return raw.data.merchant
    }
    
    // Check rawApiResponse
    if (raw.rawApiResponse?.data?.[type]?.account?.name) {
      return raw.rawApiResponse.data[type].account.name
    }
    
    // Check nested with language
    const nameObj = raw.data?.data?.[type]?.account?.name
    if (nameObj?.th) return nameObj.th
    if (nameObj?.en) return nameObj.en
    
    return ''
  } catch {
    return ''
  }
}

// ============================================
// Service
// ============================================

export const SlipLogService = {
  /**
   * ดึงรายการสลิปทั้งหมด
   */
  async getAll(
    sessionToken: string,
    params: SlipLogQueryParams = {}
  ): Promise<SlipLogQueryResult> {
    const { page = 1, limit = 25, sort } = params
    
    console.log('🔍 [SlipLogService] getAll:', params)
    
    // Build where clause - เหมือน Angular: query.doesNotExist('deletedAt')
    const where: Record<string, any> = {
      deletedAt: { $exists: false },
    }
    
    // Status filters
    if (params.status) {
      where.status = params.status
    }
    if (params.verificationStatus) {
      where.verificationStatus = params.verificationStatus
    }
    if (params.uploadFrom) {
      where.uploadFrom = params.uploadFrom
    }
    
    // ✅ Date range filter - ใช้ datetime field (ISO string)
    // ✅ datetime เป็น ISO string เช่น "2025-09-17T01:33:10+07:00"
    if (params.startDate || params.endDate) {
      if (params.startDate) {
        const startDateObj = new Date(params.startDate);
        // ✅ Format เป็น ISO string (เก็บ timezone ไว้)
        const startDateStr = startDateObj.toISOString();
        where.datetime = {
          ...where.datetime,
          $gte: startDateStr,
        };
      }
      if (params.endDate) {
        const endDateObj = new Date(params.endDate);
        // ✅ Format เป็น ISO string (เก็บ timezone ไว้)
        const endDateStr = endDateObj.toISOString();
        where.datetime = {
          ...where.datetime,
          $lte: endDateStr,
        };
      }
      
      console.log('📅 [SlipLogService] Date filter:', {
        startDate: params.startDate,
        endDate: params.endDate,
        whereDateTime: where.datetime,
      });
    }
    
    // Query options
    const queryOptions: any = {
      where,
      limit,
      skip: (page - 1) * limit,
      order: sort?.direction === 'asc' ? sort.field : `-${sort?.field || 'createdAt'}`,
      count: 1,
    }
    
    console.log('📡 [SlipLogService] Query options:', queryOptions)
    
      // ✅ Include member_id (mamer_id) เพื่อดึงข้อมูล member
      queryOptions.include = ['mamer_id']

      const response = await queryClass('SlipLog', queryOptions, sessionToken)

      console.log('✅ [SlipLogService] Result:', {
      total: response.count,
      itemsCount: response.results?.length,
      firstItem: response.results?.[0],
    })
    
    return {
      results: (response.results || []).map(mapToSlipLog),
      count: response.count || 0,
      page,
      limit,
    }
  },
  
  /**
   * ดึงสลิปตาม ID
   */
  async getById(id: string, sessionToken: string): Promise<SlipLog | null> {
    const queryOptions = {
      where: {
        objectId: id,
        deletedAt: { $exists: false },
      },
      include: ['mamer_id'],
    }
    
    const response = await queryClass('SlipLog', queryOptions, sessionToken)
    
    if (response.results.length === 0) {
      return null
    }
    
    return mapToSlipLog(response.results[0])
  },
  
  /**
   * อัปโหลดสลิป (Panel)
   */
  async uploadSlip(
    data: UploadSlipData,
    sessionToken: string
  ): Promise<{ success: boolean; message: string; slipLog?: SlipLog }> {
    const formData = new FormData()
    formData.append('slipImage', data.slipImage)
    formData.append('member_id', data.member_id)
    formData.append('qr_text', data.qr_text || 'panel')

    // Get API URL from env
    const apiUrl = import.meta.env.VITE_FRONT_API_URL || ''
    const uploadUrl = `${apiUrl}/api/v2/frontend-app/upload-slip-panel`

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        return {
          success: false,
          message: error.error || 'อัปโหลดสลิปไม่สำเร็จ',
        }
      }

      const result = await response.json()
      return {
        success: true,
        message: 'อัปโหลดสลิปสำเร็จ',
        slipLog: result.slipLog ? mapToSlipLog(result.slipLog) : undefined,
      }
    } catch (error) {
      console.error('Upload slip error:', error)
      return {
        success: false,
        message: 'เกิดข้อผิดพลาดในการอัปโหลดสลิป',
      }
    }
  },

  /**
   * Smart Slip Upload - อัปโหลดสลิปพร้อมการจับคู่สมาชิกอัตโนมัติ
   * ระบบจะอ่านข้อมูลจากสลิปอัตโนมัติผ่าน Slip API (EasySlip/Slip2Go)
   */
  async uploadSlipSmart(
    slipImage: File,
    sessionToken: string
  ): Promise<SmartSlipUploadResult> {
    const formData = new FormData()
    formData.append('slipImage', slipImage)
    // Note: ไม่ต้องส่ง sender_account หรือข้อมูลอื่น
    // เพราะ backend จะอ่านจาก Slip API โดยอัตโนมัติ

    // Get API URL from env
    const apiUrl = import.meta.env.VITE_FRONT_API_URL || ''
    const uploadUrl = `${apiUrl}/api/v2/slip/upload-and-match`

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'อัปโหลดสลิปไม่สำเร็จ')
      }

      const result = await response.json()
      return result
    } catch (error) {
      console.error('Smart upload slip error:', error)
      throw error
    }
  },
  
  /**
   * คำนวณสถิติสลิป
   */
  calculateStats(slips: SlipLog[]): SlipLogStats {
    return {
      totalSlips: slips.length,
      approvedSlips: slips.filter(s => s.status === 'Approved').length,
      pendingSlips: slips.filter(s => s.status === 'Pending').length,
      rejectedSlips: slips.filter(s => s.status === 'Rejected').length,
      duplicateSlips: slips.filter(s => (s.slip_count || 0) > 1).length,
      totalAmount: slips.reduce((sum, s) => sum + (s.amount || 0), 0),
    }
  },
}

